import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { change, Field, formValueSelector, reduxForm } from "redux-form"
import {
  createBooking,
  getBookingCalendars,
  getSubCalendars,
  hideCreateBookingModal,
  hideCreateBookingWarningModal,
  setBookingCheckoutSession,
  updateBooking,
} from "store/actions"
import { FORM_NAMES } from "constants/index"
import { Col, Row } from "reactstrap"
import {
  RadioField,
  SingleSelectField,
  TextField,
} from "components/RenderInput"
import { getCurrentDate, getTimezones } from "utils"
import { SaveButton } from "components/Buttons"
import validate from "./createBookingFormValidation"
import { mapBookingFormToApi } from "./utils"
import ModalComponent from "components/Modals"

const CreateBookingForm = ({
  allCalendars,
  getSubCalendars,
  subCalendars,
  change,
  handleSubmit,
  minuteValue,
  hideCreateBookingModal,
  createBookingLoading,
  vendorId,
  userId,
  createBooking,
  hideCreateBookingWarningModal,
  createBookingWarningModalIsOpen,
  errorMessage,
  getBookingCalendars,
  setBookingCalendar,
  setBookingSubCalendar,
  initialCalendarId,
  subCalendar,
  bookingCheckoutSession,
  setBookingCheckoutSession,
  isEditMode = false,
  bookedSeats,
  updateBooking,
  selectedBooking = {},
  hideEditModal,
}) => {
  useEffect(() => {
    if (initialCalendarId) {
      getSubCalendars({ calendar_ids: initialCalendarId })
    }
    return () => {
      setBookingCalendar(null)
      setBookingSubCalendar(null)
      setBookingCheckoutSession(null)
    }
  }, [])

  const handleCreateBooking = data => {
    if (isEditMode) {
      updateBooking({
        values: {
          ...selectedBooking,
          ...mapBookingFormToApi({ ...data, userId, vendorId }),
        },
        callback: () => {
          getBookingCalendars({ type: 2 })
          hideEditModal()
        },
      })
    } else {
      createBooking({
        values: mapBookingFormToApi({ ...data, userId, vendorId }),
        callback: () => {
          getBookingCalendars({ type: 2 })
        },
      })
    }
  }

  useEffect(() => {
    if (subCalendar) {
      setSelectedSubCalendar(
        subCalendars?.find(calendar => calendar?.id === subCalendar)
      )
    }
  }, [subCalendar, subCalendars])

  const calcluateMaxDays = daysInAdvance => {
    const endDate = new Date(new Date().getTime() + daysInAdvance * 86400000)
    return `${endDate.getFullYear()}-${
      endDate.getMonth() < 9
        ? "0" + (endDate.getMonth() + 1)
        : endDate.getMonth() + 1
    }-${endDate.getDate() < 10 ? "0" + endDate.getDate() : endDate.getDate()}`
  }

  const [selectedSubCalendar, setSelectedSubCalendar] = useState(null)

  return (
    <React.Fragment>
      {bookingCheckoutSession ? (
        <>
          <Row>
            <Col>
              <h5>
                If the checkout screen doesn't pop up, click the button below.
              </h5>
            </Col>
          </Row>
          <Row className="my-2">
            <Col xs="auto">
              <SaveButton
                type="button"
                content="CHECKOUT"
                onClick={() => {
                  window.open(bookingCheckoutSession?.checkout_session?.url)
                }}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <p>
                Once the payment is confirmed, the booking will be created, and
                you will receive an email.
              </p>
            </Col>
          </Row>
          <Row className="d-flex justify-content-end">
            <Col xs="auto">
              <SaveButton
                type="button"
                color="light"
                content="OK"
                onClick={hideCreateBookingModal}
              />
            </Col>
          </Row>
        </>
      ) : (
        <form>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>
                <span style={{ color: "red" }}>*</span>Calendar:
              </label>
            </Col>
            <Col xs="auto">
              <div style={{ minWidth: "300px" }}>
                <Field
                  name="calendar"
                  component={SingleSelectField}
                  options={allCalendars?.map(calendar => ({
                    value: calendar?.id,
                    label: calendar?.name,
                  }))}
                  onChange={value => {
                    getSubCalendars({ calendar_ids: value })
                    change("subCalendar", null)
                    setSelectedSubCalendar(null)
                  }}
                  disabled={isEditMode}
                />
              </div>
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2">
              <label>
                <span style={{ color: "red" }}>*</span>
                Sub Calendar:
              </label>
            </Col>
            <Col xs="auto">
              <div style={{ minWidth: "300px" }}>
                <Field
                  name="subCalendar"
                  component={SingleSelectField}
                  options={subCalendars?.map(subCalendar => ({
                    value: subCalendar?.id,
                    label: subCalendar?.name,
                  }))}
                  disabled={isEditMode}
                />
              </div>
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>Location:</label>
            </Col>
            <Col className="mt-2">
              <span>
                {subCalendars?.find(x => x?.id === subCalendar)?.location || ""}
              </span>
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>
                <span style={{ color: "red" }}>*</span>
                Timezone:
              </label>
            </Col>
            <Col xs="auto">
              <div style={{ minWidth: "300px" }}>
                <Field
                  name="timezone"
                  component={SingleSelectField}
                  options={getTimezones()}
                />
              </div>
            </Col>
          </Row>
          <Row className="my-3">
            <Col
              xs={12}
              md="auto"
              className="mt-2"
              style={{ minWidth: "124px" }}
            >
              <label>
                <span style={{ color: "red" }}>*</span>Date:
              </label>
            </Col>
            <Col xs="auto" className="my-1">
              <Field
                name="date"
                component={TextField}
                type="date"
                min={getCurrentDate()}
                max={
                  selectedSubCalendar?.book_max_days_in_advance &&
                  selectedSubCalendar?.book_max_days_in_advance > 0
                    ? calcluateMaxDays(
                        selectedSubCalendar?.book_max_days_in_advance
                      )
                    : null
                }
              />
            </Col>
            <Col xs="auto" className="my-1">
              <Field
                name="hour"
                component={TextField}
                type="number"
                min="1"
                max="12"
                placeholder="HH"
                onChange={() => {
                  if (!minuteValue) {
                    change("minute", "00")
                  }
                }}
              />
            </Col>
            <Col xs="auto" className="my-1">
              <Field
                name="minute"
                component={TextField}
                type="number"
                min="0"
                max="60"
                placeholder="MM"
              />
            </Col>
            <Col xs="auto" className="my-1">
              <div style={{ minWidth: "100px" }}>
                <Field
                  name={"timeType"}
                  component={SingleSelectField}
                  placeholder="AM/PM"
                  options={[
                    { label: "AM", value: "AM" },
                    { label: "PM", value: "PM" },
                  ]}
                  isSearchable={false}
                  defaultValue={{ label: "AM", value: "AM" }}
                />
              </div>
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>
                <span style={{ color: "red" }}>*</span>Full Name:
              </label>
            </Col>
            <Col xs="auto" style={{ minWidth: "300px" }}>
              <Field
                name="name"
                component={TextField}
                placeholder="Full Name"
              />
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>
                <span style={{ color: "red" }}>*</span>Email:
              </label>
            </Col>
            <Col xs="auto" style={{ minWidth: "300px" }}>
              <Field name="email" component={TextField} placeholder="Email" />
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>Phone:</label>
            </Col>
            <Col xs="auto" style={{ minWidth: "300px" }}>
              <Field
                name="phone"
                component={TextField}
                placeholder="Phone Number"
                normalize={value => value.replace(/[^0-9]+/g, "")}
              />
            </Col>
          </Row>
          <Row className="my-3">
            <Col xs="auto" className="mt-2" style={{ minWidth: "124px" }}>
              <label>Notes:</label>
            </Col>
            <Col xs="auto" style={{ minWidth: "300px" }}>
              <Field name="notes" component={TextField} placeholder="Notes" />
            </Col>
          </Row>
          {!!selectedSubCalendar &&
            selectedSubCalendar?.max_seats_per_booking > 1 && (
              <Row className="my-3">
                <Col xs="auto" className="mt-2">
                  <label>
                    <span style={{ color: "red" }}>*</span>Number of seats
                    booked:
                  </label>
                </Col>
                <Col xs="auto" style={{ minWidth: "300px" }}>
                  <Field
                    name="bookedSeats"
                    component={TextField}
                    placeholder="Number of seats booked"
                    type="number"
                    min="1"
                    max={selectedSubCalendar?.max_seats_per_booking}
                  />
                </Col>
              </Row>
            )}
          {selectedSubCalendar && selectedSubCalendar?.total_price > 0 && (
            <>
              <Row className="my-3">
                <Col xs="auto" style={{ minWidth: "124px" }}>
                  <label>
                    {!!bookedSeats && bookedSeats > 1
                      ? "Price per seat:"
                      : "Price:"}
                  </label>
                </Col>
                <Col>
                  <span>${selectedSubCalendar?.total_price}</span>
                </Col>
              </Row>
              {!!bookedSeats && bookedSeats > 1 && (
                <Row className="my-3">
                  <Col xs="auto" style={{ minWidth: "124px" }}>
                    <label>Total Price: </label>
                  </Col>
                  <Col>
                    <span>
                      $
                      {parseFloat(
                        selectedSubCalendar?.total_price * bookedSeats
                      ).toFixed(2)}
                    </span>
                  </Col>
                </Row>
              )}
              {!isEditMode && (
                <>
                  <Row className="mt-3">
                    <Col xs="auto">
                      <label>
                        Choose if you will pay for the booking or if you will
                        create it without paying.
                      </label>
                    </Col>
                  </Row>
                  <Row className="mt-1">
                    <Col>
                      <Field
                        name="paymentType"
                        value="0"
                        type="radio"
                        component={RadioField}
                        label="Create it for free"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Field
                        name="paymentType"
                        value="1"
                        type="radio"
                        component={RadioField}
                        label="Require payment"
                      />
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
          <Row className="d-flex justify-content-end">
            <Col xs="auto">
              <SaveButton
                type="button"
                color="light"
                content="Cancel"
                onClick={hideCreateBookingModal}
              />
            </Col>
            <Col xs="auto">
              <SaveButton
                type="button"
                content="Save"
                onClick={handleSubmit(handleCreateBooking)}
                loading={createBookingLoading}
              />
            </Col>
          </Row>
        </form>
      )}
      <ModalComponent open={createBookingWarningModalIsOpen} title="Oops...">
        <Row>
          <Col>
            <h5>{errorMessage}</h5>
          </Col>
        </Row>
        <Row className="my-1 d-flex justify-content-end">
          <Col xs="auto">
            <SaveButton
              type="button"
              onClick={hideCreateBookingWarningModal}
              content="OK"
            />
          </Col>
        </Row>
      </ModalComponent>
    </React.Fragment>
  )
}

const selector = formValueSelector(FORM_NAMES.CREATE_BOOKING)

const mapStateToProps = state => ({
  allCalendars: state.BookingsFeature.allCalendars,
  subCalendars: state.BookingsFeature.subCalendars,
  minuteValue: selector(state, "minute"),
  subCalendar: selector(state, "subCalendar"),
  createBookingLoading: state.BookingsFeature.createBookingLoading,
  vendorId: state.Login.vendorId,
  userId: state.Login.authUser.id,
  createBookingWarningModalIsOpen:
    state.BookingsFeature.createBookingWarningModalIsOpen,
  errorMessage: state.BookingsFeature.error,
  bookingCheckoutSession: state.BookingsFeature.bookingCheckoutSession,
  bookedSeats: selector(state, "bookedSeats"),
})

const mapDispatchToProps = dispatch => ({
  getSubCalendars: values => dispatch(getSubCalendars(values)),
  change: (field, value) =>
    dispatch(change(FORM_NAMES.CREATE_BOOKING, `${field}`, value)),
  hideCreateBookingModal: () => dispatch(hideCreateBookingModal()),
  createBooking: values => dispatch(createBooking(values)),
  hideCreateBookingWarningModal: () =>
    dispatch(hideCreateBookingWarningModal()),
  getBookingCalendars: values => dispatch(getBookingCalendars(values)),
  setBookingCheckoutSession: values =>
    dispatch(setBookingCheckoutSession(values)),
  updateBooking: values => dispatch(updateBooking(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: FORM_NAMES.CREATE_BOOKING,
    validate,
  })(CreateBookingForm)
)
